import type { LatLng, PlaceResult, PriceLevel } from './types';

const EARTH_RADIUS_M = 6_371_000;

export function haversineMetres(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Bayesian average, normalised to 0..1.
 *
 * A raw mean lets a 5.0-from-3-reviews place outrank a 4.6-from-800, which is exactly
 * the wrong answer for "where should we eat". `m` is how many reviews of evidence it
 * takes to move a place off the prior `C`.
 */
export const RATING_PRIOR_WEIGHT = 20;
export const RATING_PRIOR_MEAN = 4.0;

export function bayesianRating(rating?: number, count?: number): number {
  if (rating === undefined) return (RATING_PRIOR_MEAN - 1) / 4;
  const v = count ?? 0;
  const adjusted =
    (v * rating + RATING_PRIOR_WEIGHT * RATING_PRIOR_MEAN) /
    (v + RATING_PRIOR_WEIGHT);
  // Google ratings run 1.0-5.0.
  return Math.min(1, Math.max(0, (adjusted - 1) / 4));
}

export interface ScoredPlace extends PlaceResult {
  distanceM: number;
  score: number;
}

export interface RankOptions {
  center: LatLng;
  radiusM: number;
  priceLevels?: PriceLevel[];
  openNowRequested?: boolean;
}

/** `locationBias` is only a hint, so anything well outside the asked-for radius is dropped. */
export const RADIUS_SLACK = 1.25;

const WEIGHTS = {
  relevance: 0.35,
  rating: 0.3,
  proximity: 0.25,
  price: 0.1,
} as const;

export const CLOSED_PENALTY = 0.5;

function priceMatch(
  place: PlaceResult,
  wanted: PriceLevel[] | undefined,
): number {
  if (!wanted || wanted.length === 0) return 1;
  if (!place.priceLevel) return 0.5; // unknown shouldn't be punished like a mismatch
  return (wanted as string[]).includes(place.priceLevel) ? 1 : 0;
}

export function rankPlaces(
  places: PlaceResult[],
  options: RankOptions,
): ScoredPlace[] {
  const maxDistance = options.radiusM * RADIUS_SLACK;
  const n = places.length;

  return places
    .map((place) => ({
      ...place,
      distanceM: haversineMetres(options.center, place.location),
    }))
    .filter((place) => place.distanceM <= maxDistance)
    .filter((place) => place.businessStatus !== 'CLOSED_PERMANENTLY')
    .map((place) => {
      const relevance = n <= 1 ? 1 : 1 - place.rank / n;
      const proximity = Math.exp(-place.distanceM / options.radiusM);

      let score =
        WEIGHTS.relevance * relevance +
        WEIGHTS.rating * bayesianRating(place.rating, place.userRatingCount) +
        WEIGHTS.proximity * proximity +
        WEIGHTS.price * priceMatch(place, options.priceLevels);

      if (options.openNowRequested && place.openNow === false) {
        score -= CLOSED_PENALTY;
      }

      return { ...place, score };
    })
    .sort((a, b) => b.score - a.score);
}

export function priceGlyph(level: string | undefined): string {
  switch (level) {
    case 'PRICE_LEVEL_FREE':
      return 'Free';
    case 'PRICE_LEVEL_INEXPENSIVE':
      return '$';
    case 'PRICE_LEVEL_MODERATE':
      return '$$';
    case 'PRICE_LEVEL_EXPENSIVE':
      return '$$$';
    case 'PRICE_LEVEL_VERY_EXPENSIVE':
      return '$$$$';
    default:
      return '';
  }
}
